import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import API from 'api/API';
import { DeviceSearchResponse, SetSearchResponse } from 'api/Responses/SearchResponses';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { SearchPartTable } from 'components/Table/SearchPartTable';
import { SearchSetTable } from 'components/Table/SearchSetTable';
import { useLanguage, useSearch, useTenant } from 'context/AppContext';
import { SEARCH_QUERY_LENGTH } from 'utils/helpers/constants';
import * as Styles from './SearchPage.styles';

const SearchPage: React.FC = () => {
  const [search] = useSearch();
  const [tenant] = useTenant();
  const [language] = useLanguage();
  const [debouncedSearchQuery] = useDebounce(search, 500);
  const [sets, setSets] = useState<SetSearchResponse[]>([]);
  const [devices, setDevices] = useState<DeviceSearchResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasSets = useMemo(() => sets.length > 0, [sets.length]);
  const hasDevices = useMemo(() => devices.length > 0, [devices.length]);
  const hasNoResults = useMemo(() => !hasSets && !hasDevices, [hasDevices, hasSets]);

  const querySets = useCallback(
    async (query?: string) => {
      if (query && query.length >= SEARCH_QUERY_LENGTH) {
        setIsLoading(true);
        try {
          // this is remaining as Max Kelsen
          const response = await API.search(query, tenant);
          // Only take the first 50 results from the search query
          setSets(response.sets);
          setDevices(response.parts);
        } catch (error) {
          API.errorHandler(error);
        }
        setIsLoading(false);
      }
    },
    [tenant],
  );

  useEffect(() => {
    querySets(debouncedSearchQuery);
  }, [debouncedSearchQuery, querySets]);

  if (isLoading)
    return (
      <Page>
        <PageSpinner />
      </Page>
    );

  if (hasNoResults) {
    return (
      <Page>
        <Styles.SearchPageContainer>{language.Header.searchNoResults}</Styles.SearchPageContainer>
      </Page>
    );
  }
  return (
    <Page>
      <Styles.SearchPageContainer>
        {hasSets && <SearchSetTable sets={sets} />}
        {hasDevices && <SearchPartTable parts={devices} />}
      </Styles.SearchPageContainer>
    </Page>
  );
};

export default SearchPage;
